/**
 * PlaylistLibReviewBanner — gradient banner on the My Playlists tab
 * (`/playlist/default`) encouraging users with private profiles to share
 * their playlists.
 *
 * Pure presentational component: no Apollo, no Redux. i18n via
 * `connectStaticText({ storeKey: 'playlistLibReviewBanner' })`.
 *
 * Responsive layout:
 *  - < 1024px: column layout — __top row (44px icon + title/body text) above a
 *    full-width stretched CTA button.
 *  - >= 1024px: row layout — 98px icon + title/body text + CTA button.
 */

import React, { memo } from 'react'
import { connect as connectStaticText } from '../../../../StaticText/connect'
import {
  StaticTextProvider,
  useTranslatedText,
  InternationalStrings,
} from '../../../../StaticText/StaticTextContext'
import Link, { TARGET_BLANK } from '../../../../Link/index'
import GaiaLeaf from '../../../../Icon/icons/GaiaLeaf/index'
import ExternalLink from '../../../../Icon/icons/ExternalLink/index'

const BLOCK = 'playlist-lib-profile-review-banner'

export interface PlaylistLibReviewBannerProps {
  /** Determines which banner variant to show:
   *  - 'ownProfile':   "Show what you're exploring -" with a "View Playlists" CTA.
   *  - 'otherProfile': "Is your profile private?" with a "View your Profile" CTA.
   *    Used on the owner's library when their profile is private — copy nudges
   *    them to flip visibility so shared playlists become discoverable.
   */
  variant: 'ownProfile' | 'otherProfile'
  /**
   * Destination for the CTA. Parents pass the viewer's own profile URL
   * (`/profile/{loggedInUuid}`) for the `otherProfile` private-library case so
   * the "View your Profile" button lands on the viewer's profile page rather
   * than the settings form. Defaults to `/profile/edit` for backward
   * compatibility.
   */
  buttonTo?: string
  staticText: InternationalStrings
}

interface PlaylistLibReviewBannerContentProps {
  variant: 'ownProfile' | 'otherProfile'
  buttonTo: string
}

function PlaylistLibReviewBannerContent ({
  variant,
  buttonTo,
}: PlaylistLibReviewBannerContentProps) {
  const isOwnProfile = variant === 'ownProfile'

  const titleOwn = useTranslatedText({ keys: ['titleOwnProfile'] })
  const titleOther = useTranslatedText({ keys: ['titleOtherProfile'] })
  const bodyOwn = useTranslatedText({ keys: ['bodyOwnProfile'] })
  const bodyOther = useTranslatedText({ keys: ['bodyOtherProfile'] })
  const viewPlaylistsButton = useTranslatedText({ keys: ['viewPlaylistsButton'] })
  const viewProfileButton = useTranslatedText({ keys: ['viewProfileButton'] })

  const title = isOwnProfile ? titleOwn : titleOther
  const body = isOwnProfile ? bodyOwn : bodyOther
  const buttonLabel = isOwnProfile ? viewPlaylistsButton : viewProfileButton

  const modifier = isOwnProfile ? 'own-profile' : 'other-profile'

  return (
    <div className={`${BLOCK} ${BLOCK}--${modifier}`}>
      {/* Top section: icon + title/body text.
          Icon swaps: 44px below 1024px; 98px at 1024px+. */}
      <div className={`${BLOCK}__top`}>
        <GaiaLeaf
          size="44px"
          color="#FFFFFF"
          className={`${BLOCK}__icon`}
        />
        <GaiaLeaf
          size="98px"
          color="#FFFFFF"
          className={`${BLOCK}__icon-desktop`}
        />
        <div className={`${BLOCK}__text`}>
          <span className={`${BLOCK}__title`}>{title}</span>
          <p className={`${BLOCK}__body`}>{body}</p>
        </div>
      </div>
      <Link
        to={buttonTo}
        target={TARGET_BLANK}
        directLink
        className={`${BLOCK}__button`}
        style={{ color: '#ffffff' }}
      >
        <span className={`${BLOCK}__button-label`}>{buttonLabel}</span>
        <ExternalLink
          size="16px"
          color="#FFFFFF"
          className={`${BLOCK}__button-icon`}
        />
      </Link>
    </div>
  )
}

function PlaylistLibReviewBanner (props: PlaylistLibReviewBannerProps) {
  const { variant, staticText, buttonTo = '/profile/edit' } = props
  return (
    <StaticTextProvider staticText={staticText}>
      <PlaylistLibReviewBannerContent variant={variant} buttonTo={buttonTo} />
    </StaticTextProvider>
  )
}

const MemoizedPlaylistLibReviewBanner = memo(PlaylistLibReviewBanner)

export default connectStaticText({ storeKey: 'playlistLibReviewBanner' })(MemoizedPlaylistLibReviewBanner)
